import { StaffService } from './../../_services/staff.service';
import { PaginationInfo } from './../../_models/pagination-info';
import { defer, Subject, Subscription } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { Component, effect, inject, OnInit } from '@angular/core';
import { GlobalService } from '../../_services/global.service';
import { CourseService } from '../../_services/course.service';
import { parseToDate, ResponseStructure, StaffStore } from '@iq-central-front-end/share';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

@Component({
  selector: 'iq-central-front-end-my-learning',
  templateUrl: './my-learning.component.html',
  styleUrls: ['./my-learning.component.scss'],
})
export class MyLearningComponent implements OnInit {
  empty;
  courses;
  assignedCourses: any[] = [];
  assignedCoursesPaginationInfo: PaginationInfo;
  enrolledCourses: any[] = [];
  savedCourses: any[] = [];
  likedCourses: any[] = [];
  enrolledCoursesPaginationInfo: PaginationInfo;
  assignedLpPaginationInfo: PaginationInfo;
  savedCoursesPaginationInfo: PaginationInfo;
  likedCoursesPaginationInfo: PaginationInfo;
  currentDate: Date = new Date();
  assignedLp: any[] = [];
  sub: Subscription = new Subscription();
  searchQuery: any = '   ';

  private searchSubject = new Subject<string>();
  private staffStore = inject(StaffStore);

  constructor(
    private _global: GlobalService,
    private route: ActivatedRoute,
    private _staff: StaffService,
    private _courses: CourseService
  ) {
    this.subscribeToStoreChanges();

    this.sub.add(
      this.searchSubject.pipe(
        debounceTime(300),
        distinctUntilChanged()
      ).subscribe(searchTerm => {
        this.searchQuery = searchTerm;
        this.getAssignedCourses();
        this.getAssignedLearningPaths();
        this.getEnrolledCourses();
        this.getSavedCourses();
        this.getLikedCourses();
      })
    );
  }

  ngOnInit(): void {
    this.empty = false;
    this.initializeData();
  }

  initializeData() {
    // Call individual methods instead of using resolver
    this.getAssignedCourses();
    this.getAssignedLearningPaths();
    this.getEnrolledCourses();
    this.getSavedCourses();
    this.getLikedCourses();
  }

  subscribeToStoreChanges() {
    effect(() => {
      const enrolledCourses = this.staffStore.enrolledCourses$();
      if (enrolledCourses) {
        this.enrolledCourses = enrolledCourses.courses || [];
        this.enrolledCoursesPaginationInfo = {
          number: enrolledCourses.page || 0,
          size: enrolledCourses.limit || 8,
          totalElements: enrolledCourses.total || 0,
        };
      }
    });

    effect(() => {
      const assignedCourses = this.staffStore.assignedCourses$();
      if (assignedCourses) {
        this.assignedCourses = assignedCourses.assignedCourses || [];
        this.assignedCoursesPaginationInfo = {
          number: assignedCourses.page || 0,
          size: assignedCourses.limit || 8,
          totalElements: assignedCourses.total || 0,
        };
      }
    });

    effect(() => {
      const assignedLearningPaths = this.staffStore.assignedLearningPaths$();
      if (assignedLearningPaths) {
        this.assignedLp = assignedLearningPaths.learningPaths || assignedLearningPaths.assignedCourses || [];
        this.assignedLpPaginationInfo = {
          number: assignedLearningPaths.page || 0,
          size: assignedLearningPaths.limit || 8,
          totalElements: assignedLearningPaths.total || 0,
        };
      }
    });

    effect(() => {
      const savedCourses = this.staffStore.savedCourses$();
      if (savedCourses) {
        this.savedCourses = savedCourses.courses || [];
        this.savedCoursesPaginationInfo = {
          number: savedCourses.page || 0,
          size: savedCourses.limit || 8,
          totalElements: savedCourses.total || 0,
        };
      }
    });

    effect(() => {
      const likedCourses = this.staffStore.likedCourses$();
      if (likedCourses) {
        this.likedCourses = likedCourses.courses || [];
        this.likedCoursesPaginationInfo = {
          number: likedCourses.page || 0,
          size: likedCourses.limit || 8,
          totalElements: likedCourses.total || 0,
        };
      }
    });
  }

  diff_weeks(dt1) {
    console.log(diff);
    var diff = (this.currentDate.getTime() - dt1.getTime()) / 1000;

    diff /= 60 * 60 * 24 * 7;
    return Math.abs(Math.round(diff));
  }

  transformDuration(index): string {
    const duration = parseToDate(+this.assignedCourses[index].duration);
    return duration;
  }

  transformSDuration(index): string {
    const duration = parseToDate(+this.assignedCourses[index].duration);
    return duration;
  }

  getDateLeft(dueDate: string): string {
    const due = new Date(dueDate);
    const diff = due.getTime() - this.currentDate.getTime();
    const daysLeft = Math.ceil(diff / (1000 * 60 * 60 * 24));

    if (daysLeft < 0) {
      return 'Overdue';
    } else if (daysLeft === 0) {
      return 'Due today';
    } else if (daysLeft === 1) {
      return 'Due tomorrow';
    } else if (daysLeft >= 14) {
      const weeks = Math.floor(daysLeft / 7);
      return `${weeks} week${weeks > 1 ? 's' : ''} left`;
    } else {
      return `${daysLeft} day${daysLeft > 1 ? 's' : ''} left`;
    }
  }

  navigateEnrolledCoursePage(page) {
    this.getEnrolledCourses(page);
  }

  getEnrolledCourses(page: number = 1) {
    const payload = {
      pageNumber: page,
      pageSize: 5,
      searchQuery: this.searchQuery,
    }
    this.sub.add(
      defer(() =>
        this._staff.getEnrolledCourses(payload)
      ).subscribe()
    );
  }

  getAssignedCourses(page: number = 1) {
    const payload = {
      pageNumber: page,
      pageSize: 5,
      searchQuery: this.searchQuery,
    }
    this.sub.add(
      defer(() =>
        this._staff.getAssignedCourses(payload)
      ).subscribe()
    );
  }

  getAssignedLearningPaths(page: number = 1) {
    const payload = {
      pageNumber: page,
      pageSize: 5,
      searchQuery: this.searchQuery,
    }
    this.sub.add(
      defer(() =>
        this._staff.getAssignedLearningPath(payload)
      ).subscribe()
    );
  }

  getSavedCourses(page: number = 1) {
    const payload = {
      pageNumber: page,
      pageSize: 5,
      searchQuery: this.searchQuery,
    }
    this.sub.add(
      defer(() =>
        this._staff.getSavedCourses(payload)
      ).subscribe()
    );
  }

  getLikedCourses(page: number = 1) {
    const payload = {
      pageNumber: page,
      pageSize: 5,
      searchQuery: this.searchQuery,
    }
    this.sub.add(
      defer(() =>
        this._staff.getLikedCourses(payload)
      ).subscribe()
    );
  }

  onSearch(searchTerm: string): void {
    this.searchSubject.next(searchTerm);
  }

  navigateLpPage(page) {
    this.getAssignedLearningPaths(page);
  }

  navigateAssignedCoursePage(page) {
    this.getAssignedCourses(page);
  }

  navigateSavedCoursePage(page) {
    this.getSavedCourses(page);
  }

  removeSavedCourse(course) {
    const payload = this.savedCourses;
    this.sub.remove(
      this._courses.toggleSavedCourse(course.code, payload).subscribe({
        next: (res: ResponseStructure) => {
          this.savedCourses = this.savedCourses.filter(
            (m) => m.id !== course.id
          );
          this._global.globalSuccessHandler(res);
        },
      })
    );
  }
  z;
}
